/**
 * @fileoverview Ultra-reactive mobile frontend component for stadium fans playing "Farmeando Aura".
 * Provides high-speed touch event processing, batched WebSocket tap transmission (every 100ms),
 * dynamic theme styling, side selection, real-time state synchronization, and Screen WakeLock management.
 * 
 * @module components/Dashboard/Sections/FarmeandoAuraFan
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    ConnectWebSocketForFarmeandoAuraFan,
    DisconnectWebSocketForFarmeandoAuraFan,
    SendFarmeandoAuraTapBatch,
    SendFarmeandoAuraJoinSide,
    RequestFarmeandoAuraState
} from '../../../utilities/WS/FarmeandoAuraFanWS';
import { GetConfig } from '../../../utilities/BL/FarmeandoAura';

/**
 * Key stored in sessionStorage to remember the fan's chosen team side.
 * @constant {string}
 */
const SESSION_STORAGE_SIDE_KEY = 'farmeandoAura.selectedSide';

/**
 * Tap flush interval threshold in milliseconds for batched WebSocket dispatch.
 * @constant {number}
 */
const BATCH_DISPATCH_INTERVAL_MILLISECONDS = 100;

/**
 * Maximum taps threshold to trigger an immediate batch flush before the timer expires.
 * @constant {number}
 */
const MAXIMUM_TAPS_PER_IMMEDIATE_BATCH = 6;

/**
 * Collection of emojis for floating aura particles spawned on touch interactions.
 * @constant {Array<string>}
 */
const AURA_FLOATING_EMOJIS = ['⚡', '🔥', '✨', '💥', '🌟', '💫', '☄️'];

/**
 * Default fallback colors when no theme is provided.
 * @constant {Object}
 */
const DEFAULT_THEME_COLORS = {
    player1Color: '#00ffcc',
    player2Color: '#ff0055',
    fanBackgroundColor: '#0a0a14',
    fanTextColor: '#ffffff'
};

/**
 * FarmeandoAuraFan Component
 * 
 * @function FarmeandoAuraFan
 * @returns {React.ReactElement} The rendered mobile fan interface.
 */
export const FarmeandoAuraFan = () => {
    // =========================================================================
    // 1. ESTADOS DE SESIÓN Y SELECCIÓN DE BANDO
    // =========================================================================
    const [selectedPlayerSide, setSelectedPlayerSide] = useState(() => {
        return sessionStorage.getItem(SESSION_STORAGE_SIDE_KEY) || '';
    });

    // =========================================================================
    // 2. ESTADOS DE RED Y CONECTIVIDAD
    // =========================================================================
    const [webSocketConnectionStatus, setWebSocketConnectionStatus] = useState('idle');

    // =========================================================================
    // 3. ESTADOS DE LA PARTIDA Y SINCRONIZACIÓN CON EL SERVIDOR
    // =========================================================================
    const [currentEngineState, setCurrentEngineState] = useState('idle'); // idle, cover, game_ready, countdown, playing, paused, finished
    const [roundCountdownValue, setRoundCountdownValue] = useState(3);
    const [roundWinnerResult, setRoundWinnerResult] = useState(null); // 'J1', 'J2', 'Empate'
    const [activeThemeData, setActiveThemeData] = useState(null);

    // =========================================================================
    // 4. REFERENCIAS MUTABLES PARA ALTO RENDIMIENTO (BATCHING & WAKELOCK)
    // =========================================================================
    const tapAccumulatorBufferRef = useRef({
        accumulatedTapCount: 0,
        dispatchTimerIdentifier: null,
        isDispatching: false
    });

    const screenWakeLockSentinelRef = useRef(null);
    const activeCombatAvatarImageRef = useRef(null);
    const particlesContainerRef = useRef(null);

    // =========================================================================
    // 5. GESTIÓN DEL SCREEN WAKELOCK (MANTENER PANTALLA ENCENDIDA)
    // =========================================================================

    /**
     * Requests screen WakeLock to prevent the mobile screen from dimming or turning off during gameplay.
     * 
     * @async
     * @function requestScreenWakeLock
     */
    const requestScreenWakeLock = useCallback(async () => {
        try {
            if ('wakeLock' in navigator && !screenWakeLockSentinelRef.current) {
                const wakeLockSentinel = await navigator.wakeLock.request('screen');
                screenWakeLockSentinelRef.current = wakeLockSentinel;

                wakeLockSentinel.addEventListener('release', () => {
                    screenWakeLockSentinelRef.current = null;
                });
            }
        } catch (wakeLockError) {
            // WakeLock request can fail if device battery is low or document hidden; non-blocking
        }
    }, []);

    /**
     * Releases active screen WakeLock cleanly.
     * 
     * @async
     * @function releaseScreenWakeLock
     */
    const releaseScreenWakeLock = useCallback(async () => {
        try {
            if (screenWakeLockSentinelRef.current) {
                await screenWakeLockSentinelRef.current.release();
                screenWakeLockSentinelRef.current = null;
            }
        } catch (releaseError) {
            screenWakeLockSentinelRef.current = null;
        }
    }, []);

    // Re-request WakeLock when tab/app returns to visible state if in active game/waiting
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && selectedPlayerSide && currentEngineState !== 'finished') {
                requestScreenWakeLock();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [selectedPlayerSide, currentEngineState, requestScreenWakeLock]);

    // =========================================================================
    // 6. CARGA DE CONFIGURACIÓN Y TEMA INICIAL
    // =========================================================================
    useEffect(() => {
        const loadInitialThemeConfiguration = async () => {
            try {
                const configurationResponse = await GetConfig();
                if (configurationResponse.success && configurationResponse.data) {
                    if (configurationResponse.data.activeTheme) {
                        setActiveThemeData(configurationResponse.data.activeTheme);
                    }
                    if (configurationResponse.data.currentState) {
                        setCurrentEngineState(configurationResponse.data.currentState);
                    }
                }
            } catch (themeLoadingError) {
                // Non-blocking fallback to default visual styles
            }
        };

        loadInitialThemeConfiguration();
    }, []);

    // =========================================================================
    // 7. WEBSOCKET COMUNICACIÓN Y PROCESAMIENTO DE MENSAJES
    // =========================================================================

    /**
     * Dispatches queued taps in buffer to the server and resets local accumulator.
     * 
     * @function flushAccumulatedTapsToServer
     */
    const flushAccumulatedTapsToServer = useCallback(() => {
        const tapBuffer = tapAccumulatorBufferRef.current;

        if (tapBuffer.dispatchTimerIdentifier) {
            clearTimeout(tapBuffer.dispatchTimerIdentifier);
            tapBuffer.dispatchTimerIdentifier = null;
        }

        if (tapBuffer.accumulatedTapCount <= 0 || !selectedPlayerSide) {
            return;
        }

        const tapsToTransmit = tapBuffer.accumulatedTapCount;
        tapBuffer.accumulatedTapCount = 0;

        SendFarmeandoAuraTapBatch(selectedPlayerSide, tapsToTransmit);
    }, [selectedPlayerSide]);

    /**
     * Handles incoming WebSocket messages and updates game state accordingly.
     * 
     * @function handleIncomingWebSocketMessage
     * @param {Object} receivedMessage - The parsed JSON message received from the server.
     */
    const handleIncomingWebSocketMessage = useCallback((receivedMessage) => {
        if (!receivedMessage || !receivedMessage.type) {
            return;
        }

        const normalizedMessageType = receivedMessage.type.toLowerCase();

        switch (normalizedMessageType) {
            case 'aura_snapshot': {
                const snapshotState = receivedMessage.state || {};
                if (snapshotState.state) {
                    setCurrentEngineState(snapshotState.state);
                }
                if (snapshotState.currentTheme) {
                    setActiveThemeData(snapshotState.currentTheme);
                }
                if (snapshotState.winner) {
                    setRoundWinnerResult(snapshotState.winner);
                }
                break;
            }

            case 'aura_show_cover': {
                setCurrentEngineState('cover');
                if (receivedMessage.state?.currentTheme) {
                    setActiveThemeData(receivedMessage.state.currentTheme);
                }
                setRoundWinnerResult(null);
                break;
            }

            case 'aura_show_game': {
                setCurrentEngineState('game_ready');
                if (receivedMessage.state?.currentTheme) {
                    setActiveThemeData(receivedMessage.state.currentTheme);
                }
                setRoundWinnerResult(null);
                break;
            }

            case 'aura_countdown': {
                setCurrentEngineState('countdown');
                setRoundCountdownValue(receivedMessage.count !== undefined ? receivedMessage.count : 3);
                setRoundWinnerResult(null);
                break;
            }

            case 'aura_round_started': {
                setCurrentEngineState('playing');
                setRoundWinnerResult(null);
                break;
            }

            case 'aura_paused': {
                setCurrentEngineState('paused');
                break;
            }

            case 'aura_resumed': {
                setCurrentEngineState('playing');
                break;
            }

            case 'aura_game_over': {
                flushAccumulatedTapsToServer();
                setCurrentEngineState('finished');
                setRoundWinnerResult(receivedMessage.winner || 'Empate');
                releaseScreenWakeLock();
                break;
            }

            case 'aura_reset': {
                flushAccumulatedTapsToServer();
                setCurrentEngineState('idle');
                setRoundWinnerResult(null);
                break;
            }

            default:
                break;
        }
    }, [flushAccumulatedTapsToServer, releaseScreenWakeLock]);

    // WebSocket lifecycle connection effect
    useEffect(() => {
        if (!selectedPlayerSide) {
            DisconnectWebSocketForFarmeandoAuraFan();
            return;
        }

        ConnectWebSocketForFarmeandoAuraFan(
            selectedPlayerSide,
            handleIncomingWebSocketMessage,
            (newConnectionStatus) => setWebSocketConnectionStatus(newConnectionStatus)
        );

        requestScreenWakeLock();

        return () => {
            flushAccumulatedTapsToServer();
            DisconnectWebSocketForFarmeandoAuraFan();
            releaseScreenWakeLock();

            const tapBuffer = tapAccumulatorBufferRef.current;
            if (tapBuffer.dispatchTimerIdentifier) {
                clearTimeout(tapBuffer.dispatchTimerIdentifier);
                tapBuffer.dispatchTimerIdentifier = null;
            }
        };
    }, [selectedPlayerSide, handleIncomingWebSocketMessage, flushAccumulatedTapsToServer, requestScreenWakeLock, releaseScreenWakeLock]);

    // =========================================================================
    // 8. EFECTOS VISUALES DIRECTOS EN DOM (PARTÍCULAS Y RIPPLES)
    // =========================================================================

    /**
     * Spawns a glowing tap ripple effect at the touch coordinates.
     * 
     * @function spawnTapRippleEffect
     * @param {number} coordinateX - Horizontal coordinate in pixels.
     * @param {number} coordinateY - Vertical coordinate in pixels.
     */
    const spawnTapRippleEffect = (coordinateX, coordinateY) => {
        const rippleElement = document.createElement('div');
        rippleElement.className = 'aura-fan__tap-ripple';
        rippleElement.style.left = `${coordinateX}px`;
        rippleElement.style.top = `${coordinateY}px`;

        const targetContainer = particlesContainerRef.current || document.body;
        targetContainer.appendChild(rippleElement);

        setTimeout(() => {
            rippleElement.remove();
        }, 320);
    };

    /**
     * Spawns a floating aura emoji / spark particle flying upwards from the touch location.
     * 
     * @function spawnFloatingAuraParticle
     * @param {number} coordinateX - Horizontal coordinate in pixels.
     * @param {number} coordinateY - Vertical coordinate in pixels.
     */
    const spawnFloatingAuraParticle = (coordinateX, coordinateY) => {
        const particleElement = document.createElement('div');
        particleElement.className = 'aura-fan__floating-particle';
        particleElement.innerText = AURA_FLOATING_EMOJIS[Math.floor(Math.random() * AURA_FLOATING_EMOJIS.length)];

        const randomizedDeltaX = (Math.random() - 0.5) * 160;
        const randomizedDeltaY = -(Math.random() * 180 + 80);
        const randomizedRotation = (Math.random() - 0.5) * 90;

        particleElement.style.setProperty('--target-x', `${randomizedDeltaX}px`);
        particleElement.style.setProperty('--target-y', `${randomizedDeltaY}px`);
        particleElement.style.setProperty('--target-rotation', `${randomizedRotation}deg`);
        particleElement.style.left = `${coordinateX}px`;
        particleElement.style.top = `${coordinateY}px`;

        const targetContainer = particlesContainerRef.current || document.body;
        targetContainer.appendChild(particleElement);

        setTimeout(() => {
            particleElement.remove();
        }, 750);
    };

    // =========================================================================
    // 9. CONTROLADOR DE TAPS ULTRA-REACTIVO
    // =========================================================================

    /**
     * Handles high-speed touch and pointer interactions on the screen combat area.
     * 
     * @function handlePointerDownTap
     * @param {React.PointerEvent|React.TouchEvent|React.MouseEvent} pointerInteractionEvent
     */
    const handlePointerDownTap = (pointerInteractionEvent) => {
        if (currentEngineState !== 'playing' || !selectedPlayerSide) {
            return;
        }

        // Haptic feedback for tactical mobile sensory experience
        if ('vibrate' in navigator && typeof navigator.vibrate === 'function') {
            try {
                navigator.vibrate(12);
            } catch (vibrationError) {
                // Safe ignore if vibration is restricted
            }
        }

        const touchCoordinateX = pointerInteractionEvent.clientX !== undefined
            ? pointerInteractionEvent.clientX
            : (pointerInteractionEvent.touches && pointerInteractionEvent.touches[0] ? pointerInteractionEvent.touches[0].clientX : window.innerWidth / 2);

        const touchCoordinateY = pointerInteractionEvent.clientY !== undefined
            ? pointerInteractionEvent.clientY
            : (pointerInteractionEvent.touches && pointerInteractionEvent.touches[0] ? pointerInteractionEvent.touches[0].clientY : window.innerHeight / 2);

        // Immediate visual DOM effects
        spawnTapRippleEffect(touchCoordinateX, touchCoordinateY);
        spawnFloatingAuraParticle(touchCoordinateX, touchCoordinateY);

        // Mascot aura reaction animation
        if (activeCombatAvatarImageRef.current) {
            activeCombatAvatarImageRef.current.classList.add('aura-fan__combat__avatar-img--hit');
            setTimeout(() => {
                if (activeCombatAvatarImageRef.current) {
                    activeCombatAvatarImageRef.current.classList.remove('aura-fan__combat__avatar-img--hit');
                }
            }, 60);
        }

        // Increment tap batch accumulator
        const tapBuffer = tapAccumulatorBufferRef.current;
        tapBuffer.accumulatedTapCount += 1;

        if (tapBuffer.accumulatedTapCount >= MAXIMUM_TAPS_PER_IMMEDIATE_BATCH) {
            flushAccumulatedTapsToServer();
            return;
        }

        if (!tapBuffer.dispatchTimerIdentifier) {
            tapBuffer.dispatchTimerIdentifier = setTimeout(
                flushAccumulatedTapsToServer,
                BATCH_DISPATCH_INTERVAL_MILLISECONDS
            );
        }
    };

    // =========================================================================
    // 10. ACCIONES DE USUARIO: SELECCIÓN Y SALIDA
    // =========================================================================

    /**
     * Selects a team side, persists it to sessionStorage, and joins the channel.
     * 
     * @function handlePickSide
     * @param {string} chosenSide - Team side ('J1' or 'J2').
     */
    const handlePickSide = (chosenSide) => {
        sessionStorage.setItem(SESSION_STORAGE_SIDE_KEY, chosenSide);
        setSelectedPlayerSide(chosenSide);
        requestScreenWakeLock();
    };

    /**
     * Resets chosen side, removes from sessionStorage, releases WakeLock, and returns to selection view.
     * 
     * @function handleSwitchSideOrLogout
     */
    const handleSwitchSideOrLogout = () => {
        flushAccumulatedTapsToServer();
        sessionStorage.removeItem(SESSION_STORAGE_SIDE_KEY);
        setSelectedPlayerSide('');
        releaseScreenWakeLock();
        DisconnectWebSocketForFarmeandoAuraFan();
    };

    // =========================================================================
    // 11. HELPERS VISUALES (MEDIA Y COLORES)
    // =========================================================================

    /**
     * Resolves a media file path to an absolute URL against the backend API endpoint.
     * 
     * @function getResolvedMediaUrl
     * @param {string|null} mediaPath - Relative or absolute media path.
     * @returns {string} Fully resolved media URL.
     */
    const getResolvedMediaUrl = (mediaPath) => {
        if (!mediaPath) return '';
        if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://')) return mediaPath;
        const apiBaseEndpointUrl = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        const sanitizedPath = mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath;
        return `${apiBaseEndpointUrl}${sanitizedPath}`;
    };

    const teamPrimaryColor = selectedPlayerSide === 'J2'
        ? (activeThemeData?.player2Color || DEFAULT_THEME_COLORS.player2Color)
        : (activeThemeData?.player1Color || DEFAULT_THEME_COLORS.player1Color);

    const teamAvatarUrl = selectedPlayerSide === 'J2'
        ? (activeThemeData?.player2Avatar ? getResolvedMediaUrl(activeThemeData.player2Avatar) : '')
        : (activeThemeData?.player1Avatar ? getResolvedMediaUrl(activeThemeData.player1Avatar) : '');

    const teamWinnerAvatarUrl = selectedPlayerSide === 'J2'
        ? (activeThemeData?.player2Winner ? getResolvedMediaUrl(activeThemeData.player2Winner) : teamAvatarUrl)
        : (activeThemeData?.player1Winner ? getResolvedMediaUrl(activeThemeData.player1Winner) : teamAvatarUrl);

    // =========================================================================
    // 12. RENDERIZADOR DEL FONDO DINÁMICO
    // =========================================================================
    const renderFanThemeBackground = () => {
        const backgroundType = activeThemeData?.fanBackgroundType || 'color';
        const backgroundColor = activeThemeData?.fanBackgroundColor || DEFAULT_THEME_COLORS.fanBackgroundColor;
        const backgroundMediaUrl = activeThemeData?.fanBackgroundUrl ? getResolvedMediaUrl(activeThemeData.fanBackgroundUrl) : null;

        if (backgroundType === 'video' && backgroundMediaUrl) {
            return (
                <div className="aura-fan__background">
                    <video
                        src={backgroundMediaUrl}
                        className="aura-fan__background--video"
                        autoPlay
                        loop
                        muted
                        playsInline
                    />
                    <div className="aura-fan__background--overlay" />
                </div>
            );
        }

        if (backgroundType === 'image' && backgroundMediaUrl) {
            return (
                <div className="aura-fan__background">
                    <img
                        src={backgroundMediaUrl}
                        className="aura-fan__background--image"
                        alt="Tema Dinámico Farmeando Aura"
                    />
                    <div className="aura-fan__background--overlay" />
                </div>
            );
        }

        return (
            <div className="aura-fan__background">
                <div
                    className="aura-fan__background--color"
                    style={{ backgroundColor: backgroundColor }}
                />
                <div className="aura-fan__background--overlay" />
            </div>
        );
    };

    // =========================================================================
    // 13. DETERMINACIÓN DEL ESTADO VISUAL EFECTIVO
    // =========================================================================
    let currentEffectiveView = 'waiting';

    if (!selectedPlayerSide) {
        currentEffectiveView = 'selecting_side';
    } else if (currentEngineState === 'finished') {
        currentEffectiveView = 'finished';
    } else if (currentEngineState === 'countdown') {
        currentEffectiveView = 'countdown';
    } else if (currentEngineState === 'playing') {
        currentEffectiveView = 'playing';
    } else {
        currentEffectiveView = 'waiting';
    }

    // =========================================================================
    // 14. RENDERIZADO PRINCIPAL
    // =========================================================================
    return (
        <div className="aura-fan">
            {/* Capa de fondo dinámico */}
            {renderFanThemeBackground()}

            {/* Contenedor flotante para partículas y ripples */}
            <div ref={particlesContainerRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 40 }} />

            {/* Cabecera Superior */}
            <header className="aura-fan__header">
                <div className="aura-fan__header__logo-container">
                    {activeThemeData?.brandLogoUrl ? (
                        <img
                            src={getResolvedMediaUrl(activeThemeData.brandLogoUrl)}
                            alt="Logo Marca"
                            className="aura-fan__header__logo"
                        />
                    ) : (
                        <img
                            src="/img/page/adsl-five.png"
                            alt="Logo ADSL"
                            className="aura-fan__header__logo"
                        />
                    )}
                    {/* <h1 className="aura-fan__header__title">
                        {activeThemeData?.coverTitle || 'FARMEANDO AURA'}
                    </h1> */}
                </div>

                <div className="aura-fan__header__controls">
                    {selectedPlayerSide && (
                        <span
                            className="aura-fan__header__badge"
                            style={{
                                backgroundColor: '#fff',
                                // backgroundColor: selectedPlayerSide === 'J1'
                                //     ? (activeThemeData?.player1Color || DEFAULT_THEME_COLORS.player1Color)
                                //     : (activeThemeData?.player2Color || DEFAULT_THEME_COLORS.player2Color),
                                color: '#000'
                            }}
                        >
                            {/* {selectedPlayerSide === 'J1' ? 'LOCAL' : 'VISITANTE'} */}
                            {selectedPlayerSide === 'J1' ? 'Jugador 1' : 'Jugador 2'}
                        </span>
                    )}

                    {selectedPlayerSide && (
                        <button
                            type="button"
                            className="aura-fan__header__exit-button"
                            onClick={handleSwitchSideOrLogout}
                            title="Cambiar de equipo o salir"
                        >
                            <i className="bi bi-box-arrow-left"></i>
                        </button>
                    )}
                </div>
            </header>

            {/* ============================================================= */}
            {/* VISTA 1: SELECCIÓN DE BANDO                                   */}
            {/* ============================================================= */}
            {currentEffectiveView === 'selecting_side' && (
                <main className="aura-fan__select-side">
                    <h2 className="aura-fan__select-side__hero-title">Elige a tu personaje</h2>
                    {/* <p className="aura-fan__select-side__hero-subtitle">
                        Selecciona a qué equipo apoyarás para farmear aura y ganar en el estadio.
                    </p> */}

                    <div className="aura-fan__select-side__cards-container">
                        {/* Botón Bando Jugador 1 (Local) */}
                        <button
                            type="button"
                            className="aura-fan__select-side__card aura-fan__select-side__card--p1"
                            onClick={() => handlePickSide('J1')}
                        >
                            {activeThemeData?.player1Avatar ? (
                                <img
                                    src={getResolvedMediaUrl(activeThemeData.player1Avatar)}
                                    alt="Avatar Jugador 1"
                                    className="aura-fan__select-side__card__avatar"
                                    onError={(imageErrorEvent) => { imageErrorEvent.target.style.display = 'none'; }}
                                />
                            ) : (
                                <div
                                    className="aura-fan__select-side__card__avatar d-flex align-items-center justify-content-center rounded-circle"
                                    style={{ backgroundColor: activeThemeData?.player1Color || DEFAULT_THEME_COLORS.player1Color }}
                                >
                                    <i className="bi bi-person-fill fs-2 text-dark"></i>
                                </div>
                            )}
                            <div className="aura-fan__select-side__card__info">
                                {/* <span className="aura-fan__select-side__card__role-badge">EQUIPO LOCAL</span> */}
                                <h3 className="aura-fan__select-side__card__team-name" style={{ color: activeThemeData?.player1Color || DEFAULT_THEME_COLORS.player1Color }}>
                                    JUGADOR 1
                                </h3>
                            </div>
                            <i className="bi bi-chevron-right aura-fan__select-side__card__arrow" style={{ color: activeThemeData?.player1Color || DEFAULT_THEME_COLORS.player1Color }}></i>
                        </button>

                        {/* Botón Bando Jugador 2 (Visitante) */}
                        <button
                            type="button"
                            className="aura-fan__select-side__card aura-fan__select-side__card--p2"
                            onClick={() => handlePickSide('J2')}
                        >
                            {activeThemeData?.player2Avatar ? (
                                <img
                                    src={getResolvedMediaUrl(activeThemeData.player2Avatar)}
                                    alt="Avatar Jugador 2"
                                    className="aura-fan__select-side__card__avatar"
                                    onError={(imageErrorEvent) => { imageErrorEvent.target.style.display = 'none'; }}
                                />
                            ) : (
                                <div
                                    className="aura-fan__select-side__card__avatar d-flex align-items-center justify-content-center rounded-circle"
                                    style={{ backgroundColor: activeThemeData?.player2Color || DEFAULT_THEME_COLORS.player2Color }}
                                >
                                    <i className="bi bi-person-fill fs-2 text-dark"></i>
                                </div>
                            )}
                            <div className="aura-fan__select-side__card__info">
                                {/* <span className="aura-fan__select-side__card__role-badge">EQUIPO VISITANTE</span> */}
                                <h3 className="aura-fan__select-side__card__team-name" style={{ color: activeThemeData?.player2Color || DEFAULT_THEME_COLORS.player2Color }}>
                                    JUGADOR 2
                                </h3>
                            </div>
                            <i className="bi bi-chevron-right aura-fan__select-side__card__arrow" style={{ color: activeThemeData?.player2Color || DEFAULT_THEME_COLORS.player2Color }}></i>
                        </button>
                    </div>
                </main>
            )}

            {/* ============================================================= */}
            {/* VISTA 2: SALA DE ESPERA (SINCRONIZADA CON EL ESTADIO)        */}
            {/* ============================================================= */}
            {currentEffectiveView === 'waiting' && (
                <main className="aura-fan__waiting">
                    <div className="aura-fan__waiting__avatar-container">
                        <div
                            className="aura-fan__waiting__energy-ring"
                            style={{ borderColor: teamPrimaryColor }}
                        />
                        {teamAvatarUrl ? (
                            <img
                                src={teamAvatarUrl}
                                alt="Avatar de Equipo"
                                className="aura-fan__waiting__avatar"
                                onError={(imageErrorEvent) => { imageErrorEvent.target.style.display = 'none'; }}
                            />
                        ) : (
                            <div
                                className="aura-fan__waiting__avatar d-flex align-items-center justify-content-center rounded-circle"
                                style={{ backgroundColor: teamPrimaryColor }}
                            >
                                <i className="bi bi-person-fill display-4 text-dark"></i>
                            </div>
                        )}
                    </div>

                    <div
                        className="aura-fan__waiting__team-badge"
                        style={{
                            backgroundColor: teamPrimaryColor,
                            //color: '#000000',
                            borderColor: '#ffffff'
                        }}
                    >
                        {selectedPlayerSide === 'J1' ? 'JUGADOR 1' : 'JUGADOR 2'}
                    </div>

                    <h2 className="aura-fan__waiting__status-title">
                        {currentEngineState === 'cover'
                            ? '¡PREPÁRATE PARA EL COMBATE!'
                            : currentEngineState === 'game_ready'
                                //? '¡EL COMBATE VA A COMENZAR!'
                                ? 'La batalla está por comenzar...'
                                : 'ESPERANDO INICIO...'}
                    </h2>

                    {/* <p className="aura-fan__waiting__status-text">
                        Mantén la vista en la pantalla del estadio. Cuando empiece la cuenta regresiva, prepárate para tocar sin parar.
                    </p> */}
                </main>
            )}

            {/* ============================================================= */}
            {/* VISTA 3: CUENTA REGRESIVA DINÁMICA (3, 2, 1)                  */}
            {/* ============================================================= */}
            {currentEffectiveView === 'countdown' && (
                <main className="aura-fan__countdown">
                    <div className="aura-fan__countdown__number" style={{ color: teamPrimaryColor }}>
                        {roundCountdownValue > 0 ? roundCountdownValue : '¡DALE!'}
                    </div>
                    <div className="aura-fan__countdown__label">
                        {roundCountdownValue > 0 ? '¡PREPÁRATE!' : '¡A FARMEAR AURA!'}
                    </div>
                </main>
            )}

            {/* ============================================================= */}
            {/* VISTA 4: COMBATE EN VIVO (TAPPING GAME ULTRA-REACTIVO)       */}
            {/* ============================================================= */}
            {currentEffectiveView === 'playing' && (
                <main
                    className="aura-fan__combat"
                    onPointerDown={handlePointerDownTap}
                >
                    {/* Personaje / Mascota de Bando reaccionando al toque */}
                    <div className="aura-fan__combat__avatar-preview">
                        <img
                            ref={activeCombatAvatarImageRef}
                            src={teamAvatarUrl}
                            alt="Avatar de Combate"
                            className="aura-fan__combat__avatar-img"
                            onError={(imageErrorEvent) => { imageErrorEvent.target.style.display = 'none'; }}
                        />
                    </div>

                    {/* Zona central y Botón táctil gigante */}
                    <div className="aura-fan__combat__tap-zone">
                        <button
                            type="button"
                            className={`aura-fan__combat__giant-button ${selectedPlayerSide === 'J1' ? 'aura-fan__combat__giant-button--p1' : 'aura-fan__combat__giant-button--p2'}`}
                            aria-label="Farmear Aura por tu equipo"
                        >
                            <i className="bi bi-lightning-charge-fill aura-fan__combat__giant-button__icon" style={{ color: teamPrimaryColor }}></i>
                            <span className="aura-fan__combat__giant-button__text">¡FARMEA!</span>
                            <span className="aura-fan__combat__giant-button__subtext">Haz TAP TAP TAP en el botón</span>
                        </button>

                        {/* <p className="aura-fan__combat__stadium-hint">
                            ⚡ ¡Toca con máxima velocidad! Mira el estadio para ver la barra de poder.
                        </p> */}
                    </div>
                </main>
            )}

            {/* ============================================================= */}
            {/* VISTA 5: RESULTADOS DEL COMBATE (BANNER VICTORIA / DERROTA)   */}
            {/* ============================================================= */}
            {currentEffectiveView === 'finished' && (
                <main className="aura-fan__results">
                    <div
                        className={`aura-fan__results__card ${
                            roundWinnerResult === selectedPlayerSide
                                ? 'aura-fan__results__card--victory'
                                : roundWinnerResult === 'Empate'
                                    ? 'aura-fan__results__card--tie'
                                    : 'aura-fan__results__card--defeat'
                        }`}
                    >
                        {/* Avatar final */}
                        {(roundWinnerResult === selectedPlayerSide ? teamWinnerAvatarUrl : teamAvatarUrl) ? (
                            <img
                                src={roundWinnerResult === selectedPlayerSide ? teamWinnerAvatarUrl : teamAvatarUrl}
                                alt="Avatar Fin de Combate"
                                className="aura-fan__results__avatar"
                                onError={(imageErrorEvent) => { imageErrorEvent.target.style.display = 'none'; }}
                            />
                        ) : (
                            <div
                                className="aura-fan__results__avatar d-flex align-items-center justify-content-center rounded-circle mx-auto my-3"
                                style={{ width: '100px', height: '100px', backgroundColor: teamPrimaryColor }}
                            >
                                <i className="bi bi-trophy-fill fs-1 text-dark"></i>
                            </div>
                        )}

                        {/* Título de Victoria / Derrota / Empate */}
                        {roundWinnerResult === selectedPlayerSide ? (
                            <>
                                <h2 className="aura-fan__results__outcome-title aura-fan__results__outcome-title--victory">
                                    ¡Has ganado! 🏆
                                </h2>
                                {/* <p className="aura-fan__results__outcome-desc">
                                    ¡Tu bando farmeó el máximo poder de aura y dominó el estadio!
                                </p> */}
                            </>
                        ) : roundWinnerResult === 'Empate' ? (
                            <>
                                <h2 className="aura-fan__results__outcome-title aura-fan__results__outcome-title--tie">
                                    ¡Empate! ⚡
                                </h2>
                                {/* <p className="aura-fan__results__outcome-desc">
                                    Ambos equipos mostraron un poder colosal. ¡Choque de leyendas!
                                </p> */}
                            </>
                        ) : (
                            <>
                                <h2 className="aura-fan__results__outcome-title aura-fan__results__outcome-title--defeat">
                                    ¡Has perdido! 💥
                                </h2>
                                {/* <p className="aura-fan__results__outcome-desc">
                                    ¡Gran esfuerzo! Tu equipo dejó todo en el campo. ¡La próxima será la revancha!
                                </p> */}
                            </>
                        )}

                        {/* <button
                            type="button"
                            className="aura-fan__results__switch-button"
                            onClick={handleSwitchSideOrLogout}
                        >
                            <i className="bi bi-arrow-repeat"></i> Cambiar de Equipo
                        </button> */}

                        <span className="aura-fan__results__next-round-hint">
                            Esperando a que inicie una nueva ronda...
                        </span>
                    </div>
                </main>
            )}

            {/* Pie de página con créditos */}
            <footer className="aura-fan__footer" style={{ fontSize: '20px', color: '#fff' }}>
                Hecho con ♥ por Interaktia | 2026
            </footer>
        </div>
    );
};

export default FarmeandoAuraFan;